import { PropertyConditionName } from '@invenco/common-domain/enums';
import { FulfilmentServiceType, OrderCurrency } from '@invenco/sales-interface/interfaces';

import {
  type EntityQuery,
  type FieldDescriptor,
  datetime,
  entitySelect,
  number,
  optionsFromValues,
  select,
  text,
} from './field-descriptor';

/** The presentation a condition property carries before its operator is known. */
export type PropertyDefinition = {
  /** Sentence-case label shown in the property picker. */
  label: string;
  /**
   * The value input for scalar operators. The registry derives the multi-value
   * and no-value forms from this per operator, so entries describe the single
   * case only.
   */
  input: FieldDescriptor;
};

// --- Entity query helpers ---

const skuQuery: EntityQuery = ({ productsGateway }, input, options) =>
  productsGateway.getSkus(input, options);

const tagQuery: EntityQuery = ({ salesGateway }, input, options) =>
  salesGateway.getTags(input, options);

const channelQuery: EntityQuery = ({ salesGateway }, input, options) =>
  salesGateway.getChannels(input, options);

// Unlike the channel/tag/fulfilment endpoints, getShippingProfiles is unpaginated
// and returns every profile in a single response — which is what `preloadAll`
// relies on to resolve a stored id to its name.
const shippingProfileQuery: EntityQuery = ({ salesGateway }, input, options) =>
  salesGateway.getShippingProfiles(input, options);

const skuInput = entitySelect({ query: skuQuery, parentKey: 'condition-skus', valueKey: 'name' });

/**
 * One entry per `PropertyConditionName`. The `Record` annotation forces full enum
 * coverage: a missing property fails compilation. Labels are sentence-case;
 * inputs default from the property's backend type and operator semantics, with
 * overrides where a property needs a richer control (entity lookups, fixed
 * option lists).
 */
export const propertyDefinitions: Record<PropertyConditionName, PropertyDefinition> = {
  // --- Order ---
  skuName: { label: 'SKUs', input: skuInput },
  tagName: {
    label: 'Tags',
    // Tag sets can be large for big sellers, so keep this on server autocomplete
    // rather than preloading every tag.
    input: entitySelect({ query: tagQuery, parentKey: 'condition-tags', valueKey: 'name' }),
  },
  channelName: {
    label: 'Channel',
    input: entitySelect({
      query: channelQuery,
      parentKey: 'condition-channels',
      valueKey: 'name',
      preloadAll: true,
    }),
  },
  ORDER_CHANNEL_NAME: {
    label: 'Channel (multiple)',
    input: entitySelect({
      query: channelQuery,
      parentKey: 'condition-channels',
      valueKey: 'name',
      preloadAll: true,
    }),
  },
  channelCategory: { label: 'Channel category', input: text() },
  channelBrandName: { label: 'Channel brand name', input: text() },
  countryCode: { label: 'Country code', input: text() },
  ORDER_COUNTRY_CODE: { label: 'Country code (multiple)', input: text() },
  currency: {
    label: 'Currency',
    input: select({ options: optionsFromValues(Object.values(OrderCurrency)) }),
  },
  orderedAt: { label: 'Ordered at', input: datetime() },
  totalItems: { label: 'Total items', input: number({ integer: true }) },
  totalPrice: { label: 'Total price', input: number() },
  totalVolume: { label: 'Total volume', input: number() },
  ORDER_TOTAL_WEIGHT: { label: 'Total weight', input: number() },
  email: { label: 'Email', input: text() },
  ORDER_EMAIL: { label: 'Email (multiple)', input: text() },
  customerName: { label: 'Customer name', input: text() },
  ORDER_CUSTOMER_NAME: { label: 'Customer name (multiple)', input: text() },
  customerOrderNumber: { label: 'Customer order number', input: number({ integer: true }) },
  shippingAddressCustomerName: { label: 'Shipping address customer name', input: text() },
  ORDER_SHIPPING_ADDRESS_CUSTOMER_NAME: {
    label: 'Shipping address customer name (multiple)',
    input: text(),
  },
  shippingAddressEmail: { label: 'Shipping address email', input: text() },
  ORDER_SHIPPING_ADDRESS_EMAIL: { label: 'Shipping address email (multiple)', input: text() },
  shippingAddressState: { label: 'Shipping address state', input: text() },
  ORDER_SHIPPING_ADDRESS_STATE: { label: 'Shipping address state (multiple)', input: text() },
  ORDER_SHIPPING_ADDRESS_POSTCODE: { label: 'Shipping address postcode', input: text() },
  ORDER_SHIPPING_ADDRESS_POSTCODE_MULTIPLE: {
    label: 'Shipping address postcode (multiple)',
    input: text(),
  },
  billingAddressCustomerName: { label: 'Billing address customer name', input: text() },
  billingAddressEmail: { label: 'Billing address email', input: text() },
  ORDER_SHIPPING_PROFILE_ID: {
    label: 'Shipping profile',
    input: entitySelect({
      query: shippingProfileQuery,
      parentKey: 'condition-shipping-profiles',
      valueKey: 'id',
      labelKey: 'name',
      preloadAll: true,
    }),
  },
  ORDER_NAME: { label: 'Order name', input: text() },
  ORDER_ORDER_LINE_ALL_SKUS: { label: 'All SKUs', input: skuInput },
  ORDER_ORDER_LINE_NO_SKUS: { label: 'No SKUs', input: skuInput },
  ORDER_ORDER_LINE_ANY_SKUS: { label: 'At least one SKU', input: skuInput },
  ORDER_ORDER_LINE_UNIQUE_SKU_COUNT: { label: 'Unique SKU count', input: number() },
  ORDER_CUSTOM_ATTRIBUTE: { label: 'Order custom attribute', input: text() },

  // --- Warehouse shipment ---
  WAREHOUSE_SHIPMENT_WAREHOUSE_CODE: { label: 'Shipment warehouse code', input: text() },
  WAREHOUSE_SHIPMENT_TENANT_CODE: { label: 'Shipment tenant code', input: text() },
  WAREHOUSE_SHIPMENT_CHANNEL_NAME: { label: 'Channel name', input: text() },
  WAREHOUSE_SHIPMENT_CHANNEL_CATEGORY: { label: 'Channel category', input: text() },
  WAREHOUSE_SHIPMENT_CHANNEL_BRAND: { label: 'Channel brand', input: text() },
  WAREHOUSE_SHIPMENT_FULFILLMENT_SERVICE_TYPE: {
    label: 'Fulfillment service type',
    input: select({ options: optionsFromValues(Object.values(FulfilmentServiceType)) }),
  },
  WAREHOUSE_SHIPMENT_TOTAL_ITEMS: { label: 'Total items', input: number({ integer: true }) },
  WAREHOUSE_SHIPMENT_TOTAL_WEIGHT: { label: 'Total weight', input: number() },
  WAREHOUSE_SHIPMENT_TOTAL_VOLUME: { label: 'Total volume', input: number() },
  WAREHOUSE_SHIPMENT_CARRIER_SERVICE: { label: 'Carrier service', input: text() },
  WAREHOUSE_SHIPMENT_SHIPPING_ADDRESS_COUNTRY: { label: 'Shipment country', input: text() },
  WAREHOUSE_SHIPMENT_CREATED_AT: { label: 'Shipment created at', input: datetime() },
  WAREHOUSE_SHIPMENT_FULFILLMENT_ALL_SKUS: { label: 'All SKUs', input: skuInput },
  WAREHOUSE_SHIPMENT_FULFILLMENT_NO_SKUS: { label: 'No SKUs', input: skuInput },
  WAREHOUSE_SHIPMENT_FULFILLMENT_ANY_SKUS: { label: 'At least one SKU', input: skuInput },
};
